import React, { useContext } from 'react';
import { string, func } from 'prop-types';
import NikeAIShimmerButton from '../NikeAIShimmerButton';
import { StyledContainer, StyledQueryDisplay } from './styles';
import { AppContext } from '../../context/appContext';

/**
 * AskNikeAI Search Result Component
 * Displays a clickable "Ask NikeAI" option in search results
 * Shows "Ask NikeAI: {query}" or "Ask NikeAI" when no query
 * Opens the Nike AI chat interface when clicked
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.searchQuery - The current search query to display
 * @param {Function} props.onClick - Handler for when the item is clicked
 * @param {string} props.ariaLabel - Accessible label for screen readers
 * @returns {React.Element}
 */
const AskNikeAISearchResult = ({
  searchQuery = '',
  onClick,
  ariaLabel = 'Open Nike AI to ask questions about products',
}) => {
  // Access AppContext directly to close search (no props needed from parent)
  const [, actions = {}] = useContext(AppContext) || [{}, {}];
  const { setIsSearchOpen } = actions;

  const handleClick = () => {
    const nikeAI = window.webShellClient?.__WS_INTERNAL__?.nikeAI;

    // Track search entry click (variant B entry point)
    try {
      window.webShellClient?.analytics?.trackAction(
        {
          underlyingAction: {
            case: 'temporaryAction',
            value: {
              actionName: 'nike_ai-search_entry_clicked',
              type: 2,
              data: {
                has_query: searchQuery ? 'true' : 'false',
              },
            },
          },
        },
        { eventVersion: 2 },
      );
    } catch (error) {
      // Analytics non-blocking
    }

    // 1. Send message if search query provided (primary functionality)
    if (nikeAI?.open && nikeAI?.sendMessage && searchQuery) {
      nikeAI.open();
      nikeAI.sendMessage(searchQuery);
    } else if (onClick) {
      // Fallback to custom onClick if no query
      onClick();
    } else if (nikeAI?.open) {
      // Use open() instead of toggle() to avoid localStorage sync issues (TG-8150)
      // toggle() reads stale localStorage state after close, causing double-tap bug
      nikeAI.open();
    }

    // 2. Close visual search after interaction
    if (setIsSearchOpen) {
      setIsSearchOpen(false);
    }
  };

  const displayText = searchQuery ? `Ask NikeAI: ${searchQuery}` : 'Ask NikeAI';
  // Truncate for desktop (250 char limit for display only, no limit for search query itself)
  const truncatedText =
    displayText.length > 250 ? `${displayText.substring(0, 250)}...` : displayText;

  return (
    <StyledContainer
      aria-label={ariaLabel}
      data-testid="ask-nikeai-search-result"
      onClick={handleClick}
      type="button"
    >
      <NikeAIShimmerButton
        ariaLabel="Nike AI"
        asDiv={true}
        onClick={e => {
          e.stopPropagation();
          handleClick();
        }}
        size="small"
        tabIndex={-1}
        variant="static"
      />
      <StyledQueryDisplay aria-label={`Search query: ${truncatedText}`} tabIndex={-1}>
        {truncatedText}
      </StyledQueryDisplay>
    </StyledContainer>
  );
};

AskNikeAISearchResult.propTypes = {
  ariaLabel: string,
  onClick: func,
  searchQuery: string,
};

AskNikeAISearchResult.defaultProps = {
  ariaLabel: 'Open Nike AI to ask questions about products',
  onClick: undefined,
  searchQuery: '',
};

export default AskNikeAISearchResult;
